import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Tooltip,
    TableSortLabel,
    Snackbar,
    Alert,
    TablePagination,
    Chip
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    HealthAndSafety as HealthIcon // Icon for Planos
} from "@mui/icons-material";
import api from '../../services/api.js';
import Preloader from '../../components/Preloader';
import CadastroPlano from './CadastroPlano';

export default function ListaPlanos() {
    const [planos, setPlanos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Dialog Control
    const [openCadastro, setOpenCadastro] = useState(false);
    const [planoEditarId, setPlanoEditarId] = useState(null);

    const [filtroNome, setFiltroNome] = useState("");

    // Pagination & Sort
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("st_nome");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        carregarPlanos();
    }, []);

    const carregarPlanos = async () => {
        setLoading(true);
        try {
            const response = await api.get('/plano'); // Consuming our new endpoint
            setPlanos(response.data.dados || []);
        } catch (error) {
            console.error('Erro ao carregar planos:', error);
            setError('Erro ao carregar planos.');
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleOpenCadastro = (id = null) => {
        setPlanoEditarId(id);
        setOpenCadastro(true);
    };

    const handleCloseCadastro = () => {
        setOpenCadastro(false);
        setPlanoEditarId(null);
    };

    const handleSaveSuccess = (message) => {
        setSuccess(message);
        carregarPlanos();
        handleCloseCadastro();
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Tem certeza que deseja inativar este plano?")) return;
        try {
            await api.delete(`/plano/${id}`);
            setSuccess("Plano inativado com sucesso.");
            carregarPlanos();
        } catch (error) {
            setError("Erro ao inativar plano.");
        }
    };

    const formatCurrency = (valCentavos) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valCentavos / 100);
    };

    // Filter & Sort
    const planosFiltrados = planos
        .filter(p => !filtroNome || p.st_nome.toLowerCase().includes(filtroNome.toLowerCase()))
        .sort((a, b) => {
            const valA = a[orderBy] || "";
            const valB = b[orderBy] || "";

            if (typeof valA === 'string') {
                return order === 'asc'
                    ? valA.localeCompare(valB)
                    : valB.localeCompare(valA);
            }
            return order === 'asc' ? valA - valB : valB - valA;
        });

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, height: '100vh', overflow: 'auto' }}>
            <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>{error}</Alert>
            </Snackbar>
            <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>{success}</Alert>
            </Snackbar>

            <Box sx={{ margin: '0 1.5% 1.5% 1.5%', height: '90vh', display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 2, gap: 2 }}>
                    <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <HealthIcon sx={{ color: '#20ac9c', fontSize: 32 }} />
                        Gestão de Planos
                    </Typography>
                    <Button variant="contained" onClick={() => handleOpenCadastro()} sx={{ backgroundColor: '#20ac9c' }}>
                        <AddIcon /> Novo Plano
                    </Button>
                </Box>

                {/* Filters */}
                <Box sx={{ p: 2, backgroundColor: 'background.paper', mb: 2, borderRadius: 1 }}>
                    <TextField
                        size="small"
                        label="Filtrar por nome"
                        value={filtroNome}
                        onChange={(e) => setFiltroNome(e.target.value)}
                        sx={{ width: 300 }}
                    />
                </Box>

                <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto' }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <TableSortLabel active={orderBy === "st_nome"} direction={order} onClick={() => handleSort("st_nome")}>
                                        Nome
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>Mensalidade</TableCell>
                                <TableCell>Adesão</TableCell>
                                <TableCell align="center">Dependentes</TableCell>
                                <TableCell align="center">Status</TableCell>
                                <TableCell align="center">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow><TableCell colSpan={6} align="center"><Preloader /></TableCell></TableRow>
                            ) : planosFiltrados.length === 0 ? (
                                <TableRow><TableCell colSpan={6} align="center">Nenhum plano encontrado</TableCell></TableRow>
                            ) : (
                                planosFiltrados.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((plano) => (
                                    <TableRow key={plano.id_plano} hover>
                                        <TableCell>{plano.st_nome}</TableCell>
                                        <TableCell>{formatCurrency(plano.nu_valor_mensal_centavos)}</TableCell>
                                        <TableCell>{formatCurrency(plano.nu_valor_adesao_centavos)}</TableCell>
                                        <TableCell align="center">{plano.nu_limite_dependentes}</TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={plano.bo_ativo ? "Ativo" : "Inativo"}
                                                color={plano.bo_ativo ? "success" : "default"}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="Editar">
                                                <IconButton onClick={() => handleOpenCadastro(plano.id_plano)} size="small">
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Inativar">
                                                <IconButton onClick={() => handleDelete(plano.id_plano)} size="small" color="error">
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={planosFiltrados.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Box>

            {/* Dialog Component (Lazy load inside) */}
            {openCadastro && (
                <CadastroPlano
                    open={openCadastro}
                    onClose={handleCloseCadastro}
                    planoId={planoEditarId}
                    onSuccess={handleSaveSuccess}
                />
            )}
        </Box>
    );
}
